import React, { useMemo, useEffect, useContext } from 'react';
import { useQuery } from '@apollo/client';
import authenticationQuery from '../graphql/queries/authenticationQuery';
import { Redirect, Route as ReactRouterRoute } from 'react-router-dom';
import isQueryReady from '../utils/isQueryReady';
import useLocationDelay from '../hooks/useLocationDelay';
import useAnalytics, { defaultConfiguration as defaultAnalyticsConfiguration } from '../hooks/useAnalytics';
import decodeAuthenticationToken from '../utils/decodeAuthenticationToken';
import useAwaitQueries from '../hooks/useAwaitQueries';
import EnvironmentVariablesContext
  from '../contexts/EnvironmentVariablesContext';
import { useLocation } from 'react-router';
import EnsureOrientationOnMobile from "../components/EnsureOrientationOnMobile";

function makeTitle(title, suffix) {
  return title === null
    ? suffix
    : `${title} · ${suffix}`;
}

export default function Route({
  authenticated = false,
  redirectPath = '/',
  title = null,
  awaitQueries = [],
  analytics = defaultAnalyticsConfiguration,
  orientation = null,
  ...props
}) {
  const environmentVariables = useContext(EnvironmentVariablesContext);
  const authentication = useQuery(authenticationQuery);
  const delayHasBeenConsumed = useLocationDelay();
  const [readyAwaitQueries, awaitQueriesReady] = useAwaitQueries(awaitQueries);
  const location = useLocation();
  const locationPath = location.pathname + location.search + location.hash;

  const isLoadingState = (
    ! isQueryReady(authentication) ||
    ! delayHasBeenConsumed ||
    ! awaitQueriesReady
  );

  const authenticatedMismatch = (
    (authenticated === true && authentication.data.authentication.token === null) ||
    (authenticated === false && authentication.data.authentication.token !== null)
  );

  const redirectToPasswordChange = (
    authenticated &&
    authentication.data.authentication.token !== null &&
    decodeAuthenticationToken(authentication.data.authentication.token).shouldChangePassword === true &&
    props.path !== '/change-password'
  );

  const documentTitle = useMemo(function() {
    let prefix = null;

    if (typeof title === 'function') {
      if (awaitQueriesReady) {
        prefix = title(readyAwaitQueries);
      }
    } else {
      prefix = title;
    }

    return makeTitle(prefix, environmentVariables.REACT_APP_PROJECT_NAME);
  }, [awaitQueriesReady, environmentVariables.REACT_APP_PROJECT_NAME, readyAwaitQueries, title]);

  useEffect(function() {
    window.document.title = documentTitle;
  }, [documentTitle]);

  // we skip analytics if
  const skipAnalytics = useMemo(() => (
    // the consumer wants to skip
    analytics.skip ||
    // or the location is different from the given route path. This might be a
    // problem because we are going to track only "exact" matches.
    locationPath !== props.path ||
    // or we are in a loading state
    isLoadingState ||
    // or if authenticated and the route is public or vice versa
    authenticatedMismatch ||
    // or if will be redirected for a password change
    redirectToPasswordChange
  ), [analytics.skip, authenticatedMismatch, isLoadingState, locationPath, props.path, redirectToPasswordChange]);

  useAnalytics(useMemo(() => ({
    ...analytics,
    fields: (_readyAwaitQueries) => ({
      ...analytics.fields(_readyAwaitQueries),
      // We make sure the correct title is tracked, this might not be needed
      // because Google Analytics automatically gets the title from
      // `document.title` but we might be too slow to set it.
      title: documentTitle,
    }),
    skip: skipAnalytics,
  }), [analytics, documentTitle, skipAnalytics]));

  if (isLoadingState) {
    // We expect the route to handle its loading state.
    return (
      <EnsureOrientationOnMobile orientation={orientation}>
        <ReactRouterRoute {...props} />
      </EnsureOrientationOnMobile>
    );
  }

  if (authenticatedMismatch) {
    return (
      <Redirect to={{
        pathname: redirectPath,
        state: { delay: environmentVariables.REACT_APP_PAGE_TRANSITION_MS },
      }} />
    );
  }

  if (redirectToPasswordChange) {
    return (
      <Redirect to={{
        pathname: '/change-password',
        state: { delay: environmentVariables.REACT_APP_PAGE_TRANSITION_MS },
      }} />
    );
  }

  return (
    <EnsureOrientationOnMobile orientation={orientation}>
      <ReactRouterRoute {...props} />
    </EnsureOrientationOnMobile>
  );
}
